import React from 'react';
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import type { StudioDefinition, ImageLabOperationType } from '../../domain';
import { resolveStudioOperations, type OperationCatalogEntry } from './operationCatalog';

interface OperationDockProps {
  studio: StudioDefinition;
  activeKind: ImageLabOperationType;
  onSelect: (kind: ImageLabOperationType) => void;
}

/**
 * Operation dock — the user's primary "what am I doing" surface. Sources its
 * operations from `studio.featuredOperations` (spec §2.3) via the catalog so
 * Studios curate the dock; the FE no longer hardcodes a single global list.
 */
export const OperationDock: React.FC<OperationDockProps> = ({ studio, activeKind, onSelect }) => {
  const operations = resolveStudioOperations(studio);

  if (operations.length === 0) {
    return (
      <Box sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary">
          This Studio has no featured operations.
        </Typography>
      </Box>
    );
  }

  const handleChange = (_e: unknown, value: ImageLabOperationType | null) => {
    if (value) onSelect(value);
  };

  return (
    <Box
      sx={{
        px: 1.5,
        // Extra top room so the floating PRO badge isn't clipped by the
        // horizontal-scroll container.
        pt: 1.75,
        pb: 1,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflowX: 'auto',
        // Sleek: hide the scrollbar but keep scrollability.
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
      aria-label="Operation dock"
    >
      <ToggleButtonGroup
        value={activeKind}
        exclusive
        onChange={handleChange}
        size="small"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 0.75,
          // Detach the connected-button styling into separate rounded pills.
          '& .MuiToggleButtonGroup-grouped': {
            m: 0,
            px: 1.5,
            py: 0.5,
            border: theme => `1px solid ${theme.palette.divider} !important`,
            borderRadius: '999px !important',
            color: 'text.secondary',
            transition: 'background-color 120ms, color 120ms, border-color 120ms',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-selected': {
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
              borderColor: theme => `${theme.palette.primary.main} !important`,
              '&:hover': { bgcolor: 'primary.dark' },
            },
          },
        }}
      >
        {operations.map(op => (
          <DockButton key={op.kind} entry={op} />
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

interface DockButtonProps {
  entry: OperationCatalogEntry;
}

const DockButton: React.FC<DockButtonProps> = ({ entry }) => (
  <Tooltip title={entry.description} placement="top">
    <ToggleButton
      value={entry.kind}
      aria-label={entry.name}
      sx={{ position: 'relative', overflow: 'visible', textTransform: 'none' }}
    >
      {/* One-line label — never wraps; the dock scrolls horizontally instead. */}
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.4 }}
      >
        {entry.name}
      </Typography>
      {entry.isPremium && (
        // Floating PRO badge — pinned to the top-right corner, above the label
        // (zIndex) so it never pushes or wraps the text.
        <Box
          component="span"
          aria-hidden
          sx={{
            position: 'absolute',
            top: -7,
            right: -6,
            zIndex: 2,
            px: 0.5,
            borderRadius: '999px',
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            fontSize: '0.5rem',
            fontWeight: 800,
            letterSpacing: '0.04em',
            lineHeight: 1.6,
            boxShadow: 2,
            pointerEvents: 'none',
          }}
        >
          PRO
        </Box>
      )}
    </ToggleButton>
  </Tooltip>
);

/**
 * Compact horizontal-scrolling variant — useful when the dock has to live
 * inline with other top-bar controls. Mounted by `ImageLab.tsx` directly
 * for now; can be swapped for a vertical / grouped layout in F12 if needed.
 */
export const InlineOperationDock = OperationDock;

/**
 * Sentinel export: kept exposed so F12 / F14 can import the default-active
 * resolution helper without re-deriving it.
 */
export function pickDefaultOperation(studio: StudioDefinition): ImageLabOperationType | null {
  const ops = resolveStudioOperations(studio);
  return ops[0]?.kind ?? null;
}

// Re-export to keep call sites referring to a single import path.
export { Button };
